import { supabase } from '@/lib/supabaseClient';
import { KnowledgeSourceType } from './knowledgeSourceManager';
import * as pdfjsLib from 'pdfjs-dist';
import mammoth from 'mammoth';

import '@/lib/pdfWorker'; // bundled pdf.js worker (offline-safe; matches installed version)

// Types
export interface KnowledgeDocument {
  id?: string;
  title: string;
  content: string;
  file_name: string;
  file_path?: string;
  file_type: string;
  file_size?: number;
  source_type: KnowledgeSourceType;
  metadata?: Record<string, any>;
  citation_required?: boolean;
  access_level?: string;
  tenant_id: string;
  uploaded_by: string;
  is_active?: boolean;
}

export interface KnowledgeEmbedding {
  id?: string;
  document_id: string;
  source_id?: string;
  chunk_index: number;
  chunk_text: string;
  embedding?: number[];
  relevance_score?: number;
  metadata?: Record<string, any>;
  tenant_id: string;
}

export interface ProcessingResult {
  success: boolean;
  documentId?: string;
  embeddingsCount?: number;
  error?: string;
}

class KnowledgeService {
  private readonly CHUNK_SIZE = 500; // Characters per chunk
  private readonly CHUNK_OVERLAP = 50; // Overlap between chunks
  private readonly EMBEDDING_MODEL = 'text-embedding-ada-002';

  /**
   * Upload and process a document
   */
  async uploadDocument(
    file: File,
    title: string,
    sourceType: KnowledgeSourceType,
    tenantId: string | null,
    userId: string,
    metadata?: Record<string, any>
  ): Promise<ProcessingResult> {
    try {
      console.log('KnowledgeService: Starting document upload', {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        sourceType,
        tenantId
      });

      // Extract text from file
      const content = await this.extractTextFromFile(file);
      
      if (!content) {
        console.error('KnowledgeService: No content extracted from file');
        return { success: false, error: 'Could not extract text from file' };
      }

      console.log('KnowledgeService: Text extracted, length:', content.length);

      // Create document record
      const document: KnowledgeDocument = {
        title,
        content,
        file_name: file.name,
        file_type: file.type,
        file_size: file.size,
        source_type: sourceType,
        tenant_id: tenantId || '',
        uploaded_by: userId,
        is_active: true,
        metadata: {
          uploaded_at: new Date().toISOString(),
          original_name: file.name,
          ...(metadata || {})
        }
      };

      // Save document to database
      console.log('KnowledgeService: Saving document to database');
      const { data: savedDoc, error: docError } = await supabase
        .from('knowledge_documents')
        .insert(document)
        .select()
        .single();

      if (docError) {
        console.error('KnowledgeService: Error saving document:', docError);
        return { success: false, error: `Database error: ${docError.message}` };
      }

      console.log('KnowledgeService: Document saved with ID:', savedDoc.id);

      // Process embeddings
      const embeddingsResult = await this.processDocumentEmbeddings(
        savedDoc.id,
        content,
        sourceType,
        tenantId || ''
      );

      console.log('KnowledgeService: Embeddings processed, count:', embeddingsResult.count);

      return {
        success: true,
        documentId: savedDoc.id,
        embeddingsCount: embeddingsResult.count
      };
    } catch (error) {
      console.error('Error uploading document:', error);
      return { success: false, error: 'Failed to upload document' };
    }
  }

  /**
   * Extract text from various file types
   */
  async extractTextFromFile(file: File): Promise<string> {
    const fileType = file.type;
    
    // Handle text files
    if (fileType.startsWith('text/') || file.name.endsWith('.txt') || file.name.endsWith('.md')) {
      return await file.text();
    }

    // Handle JSON files
    if (fileType === 'application/json' || file.name.endsWith('.json')) {
      const text = await file.text();
      try {
        const json = JSON.parse(text);
        return JSON.stringify(json, null, 2);
      } catch {
        return text;
      }
    }

    // Handle PDF files
    if (fileType === 'application/pdf' || file.name.endsWith('.pdf')) {
      try {
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        let fullText = '';
        
        // Extract text from each page
        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          const page = await pdf.getPage(pageNum);
          const textContent = await page.getTextContent();
          const pageText = textContent.items
            .map((item: any) => item.str)
            .join(' ');
          fullText += pageText + '\n\n';
        }
        
        return fullText.trim();
      } catch (error) {
        console.error('Error extracting PDF text:', error);
        throw new Error('Failed to extract text from PDF');
      }
    }

    // Handle Word documents (DOCX)
    if (fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || 
        file.name.endsWith('.docx')) {
      try {
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer });
        return result.value;
      } catch (error) {
        console.error('Error extracting Word document text:', error);
        throw new Error('Failed to extract text from Word document');
      }
    }

    // Handle old Word documents (DOC) - not supported by mammoth
    if (fileType === 'application/msword' || file.name.endsWith('.doc')) {
      throw new Error('Old .doc format is not supported. Please convert to .docx format.');
    }

    // Unsupported file type
    throw new Error(`Unsupported file type: ${fileType}`);
  }

  /**
   * Process document into chunks and generate embeddings
   */
  async processDocumentEmbeddings(
    documentId: string,
    content: string,
    sourceType: KnowledgeSourceType,
    tenantId: string
  ): Promise<{ count: number }> {
    try {
      // Chunk the document
      const chunks = this.chunkText(content, sourceType);
      
      // Get source ID
      const { data: source } = await supabase
        .from('knowledge_sources')
        .select('id')
        .eq('source_type', sourceType)
        .single();

      const embeddings: KnowledgeEmbedding[] = [];
      
      // Process chunks in batches
      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        
        // Generate embedding for chunk
        const embedding = await this.generateEmbedding(chunk);
        
        if (embedding) {
          embeddings.push({
            document_id: documentId,
            source_id: source?.id,
            chunk_index: i,
            chunk_text: chunk,
            embedding,
            tenant_id: tenantId,
            metadata: {
              chunk_number: i + 1,
              total_chunks: chunks.length
            }
          });
        }

        // Track cost
        await this.trackCost('embedding', chunk.length, tenantId);
      }

      // Batch insert embeddings
      if (embeddings.length > 0) {
        const { error } = await supabase
          .from('knowledge_embeddings')
          .insert(embeddings);

        if (error) {
          console.error('Error saving embeddings:', error);
        }
      }

      return { count: embeddings.length };
    } catch (error) {
      console.error('Error processing embeddings:', error);
      return { count: 0 };
    }
  }

  /**
   * Chunk text into smaller pieces
   */
  chunkText(text: string, sourceType: KnowledgeSourceType): string[] {
    const chunks: string[] = [];
    
    // Source-specific chunking strategies
    if (sourceType === 'sales_training') {
      // For transcripts, try to chunk by speaker turns or timestamps
      return this.chunkByParagraphs(text);
    }

    // Default chunking by size with overlap
    let start = 0;
    while (start < text.length) {
      let end = start + this.CHUNK_SIZE;
      
      // Try to break at sentence boundary
      if (end < text.length) {
        const lastPeriod = text.lastIndexOf('.', end);
        const lastNewline = text.lastIndexOf('\n', end);
        const breakPoint = Math.max(lastPeriod, lastNewline);
        
        if (breakPoint > start + this.CHUNK_SIZE / 2) {
          end = breakPoint + 1;
        }
      }

      chunks.push(text.slice(start, end).trim());
      start = end - this.CHUNK_OVERLAP;
    }

    return chunks.filter(chunk => chunk.length > 0);
  }

  /**
   * Chunk by paragraphs (for transcripts and structured text)
   */
  private chunkByParagraphs(text: string): string[] {
    const paragraphs = text.split(/\n\n+/);
    const chunks: string[] = [];
    let currentChunk = '';

    for (const paragraph of paragraphs) {
      if (currentChunk.length + paragraph.length > this.CHUNK_SIZE) {
        if (currentChunk) {
          chunks.push(currentChunk.trim());
        }
        currentChunk = paragraph;
      } else {
        currentChunk += (currentChunk ? '\n\n' : '') + paragraph;
      }
    }

    if (currentChunk) {
      chunks.push(currentChunk.trim());
    }

    return chunks;
  }

  /**
   * Generate embedding using Supabase edge function
   */
  async generateEmbedding(text: string): Promise<number[] | null> {
    try {
      const { data, error } = await supabase.functions.invoke('generate-embedding', {
        body: { text: text }
      });

      if (error) {
        console.error('Edge function error:', error);
        return null;
      }

      if (!data || !data.embedding) {
        console.error('No embedding returned from edge function');
        return null;
      }

      return data.embedding;
    } catch (error) {
      console.error('Error generating embedding:', error);
      return null;
    }
  }

  /**
   * Process embeddings for existing documents that don't have them
   */
  async processExistingDocuments(): Promise<{ processed: number, failed: number }> {
    try {
      // Find documents without embeddings
      const { data: documents, error } = await supabase
        .from('knowledge_documents')
        .select('*')
        .is('id', 'not.null');

      if (error || !documents) {
        console.error('Error fetching documents:', error);
        return { processed: 0, failed: 0 };
      }

      let processed = 0;
      let failed = 0;

      for (const doc of documents) {
        // Check if document already has embeddings
        const { data: existingEmbeddings } = await supabase
          .from('knowledge_embeddings')
          .select('id')
          .eq('document_id', doc.id)
          .limit(1);

        if (!existingEmbeddings || existingEmbeddings.length === 0) {
          console.log(`Processing embeddings for document: ${doc.title}`);
          
          const result = await this.processDocumentEmbeddings(
            doc.id,
            doc.content,
            doc.source_type,
            doc.tenant_id
          );

          if (result.count > 0) {
            processed++;
            console.log(`✅ Generated ${result.count} embeddings for: ${doc.title}`);
          } else {
            failed++;
            console.error(`❌ Failed to generate embeddings for: ${doc.title}`);
          }
        }
      }

      return { processed, failed };
    } catch (error) {
      console.error('Error processing existing documents:', error);
      return { processed: 0, failed: 0 };
    }
  }

  /**
   * Track AI operation costs
   */
  async trackCost(
    operationType: 'embedding' | 'query' | 'completion',
    tokenCount: number,
    tenantId: string
  ) {
    // Rough token estimation (1 token ≈ 4 characters)
    const estimatedTokens = Math.ceil(tokenCount / 4);
    
    // Cost calculation (text-embedding-ada-002: $0.0001 per 1K tokens)
    const costPerThousand = operationType === 'embedding' ? 0.0001 : 0.002;
    const cost = (estimatedTokens / 1000) * costPerThousand;

    try {
      await supabase
        .from('ai_cost_tracking')
        .insert({
          tenant_id: tenantId,
          operation_type: operationType,
          model_name: this.EMBEDDING_MODEL,
          token_count: estimatedTokens,
          cost_usd: cost,
          metadata: {
            timestamp: new Date().toISOString()
          }
        });
    } catch (error) {
      console.error('Error tracking cost:', error);
    }
  }

  /**
   * Get all documents for a tenant
   */
  async getDocuments(tenantId: string, sourceType?: KnowledgeSourceType) {
    let query = supabase
      .from('knowledge_documents')
      .select('*')
      .eq('tenant_id', tenantId)
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (sourceType) {
      query = query.eq('source_type', sourceType);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching documents:', error);
      return [];
    }

    return data || [];
  }

  /**
   * Delete a document (soft delete)
   */
  async deleteDocument(documentId: string): Promise<boolean> {
    try {
      console.log('KnowledgeService: Attempting to delete document:', documentId);
      
      // First try to delete any embeddings for this document
      const { error: embeddingsError } = await supabase
        .from('knowledge_embeddings')
        .delete()
        .eq('document_id', documentId);
      
      if (embeddingsError) {
        console.warn('KnowledgeService: Could not delete embeddings:', embeddingsError.message);
        // Continue anyway, as there might not be embeddings or user might not have permission
      }
      
      // Try hard delete first
      const { error: deleteError } = await supabase
        .from('knowledge_documents')
        .delete()
        .eq('id', documentId);
      
      if (deleteError) {
        console.log('KnowledgeService: Hard delete failed, trying soft delete:', deleteError.message);
        
        // Fall back to soft delete (set is_active to false)
        const { error: updateError } = await supabase
          .from('knowledge_documents')
          .update({ is_active: false })
          .eq('id', documentId);
        
        if (updateError) {
          console.error('KnowledgeService: Both hard and soft delete failed:', updateError);
          return false;
        }
        
        console.log('KnowledgeService: Document soft deleted (is_active = false):', documentId);
        return true;
      }
      
      console.log('KnowledgeService: Document hard deleted successfully:', documentId);
      return true;
    } catch (error) {
      console.error('KnowledgeService: Exception in deleteDocument:', error);
      return false;
    }
  }

  /**
   * Update document
   */
  async updateDocument(documentId: string, updates: Partial<KnowledgeDocument>) {
    const { error } = await supabase
      .from('knowledge_documents')
      .update(updates)
      .eq('id', documentId);

    if (error) {
      console.error('Error updating document:', error);
      throw error;
    }
  }

  /**
   * Search knowledge base without embeddings (fallback text search)
   */
  async searchDocuments(query: string, tenantId: string) {
    const { data, error } = await supabase
      .from('knowledge_documents')
      .select('*')
      .eq('tenant_id', tenantId)
      .eq('is_active', true)
      .or(`title.ilike.%${query}%,content.ilike.%${query}%`)
      .limit(10);

    if (error) {
      console.error('Error searching documents:', error);
      return [];
    }

    return data || [];
  }
}

// Export singleton instance
export const knowledgeService = new KnowledgeService();